import type { CapabilityId, HumanRoleId } from './rolePreset.ts'

export const POLICY_DECISIONS = ['ALLOW', 'DELEGATED', 'APPROVAL_REQUIRED', 'DEFAULT_DENY'] as const
export type PolicyDecision = (typeof POLICY_DECISIONS)[number]

export const FIELD_VISIBILITY_CLASSES = [
  'internal',
  'client_safe',
  'creator_visible',
  'sensitive_financial',
  'private_evaluation',
] as const
export type FieldVisibilityClass = (typeof FIELD_VISIBILITY_CLASSES)[number]

export type ObjectScope = Readonly<{
  workspaceId: string
  objectType: string
  objectId: string
}>

export type PermissionPolicy = Readonly<{
  roleId: HumanRoleId
  capability: CapabilityId
  objectType: string
  action: string
  decision: Exclude<PolicyDecision, 'DEFAULT_DENY'>
  moduleId?: string
}>

export type PermissionContext = Readonly<{
  roleId: HumanRoleId
  capabilities: ReadonlySet<CapabilityId>
  actorWorkspaceId: string
  target: ObjectScope
  action: string
  enabledModules: ReadonlySet<string>
  delegatedCapabilities?: ReadonlySet<CapabilityId>
  approvalCapabilities?: ReadonlySet<CapabilityId>
}>

export type PermissionResult = Readonly<{
  decision: PolicyDecision
  reasonCode:
    | 'policy_allow'
    | 'policy_delegated'
    | 'policy_approval_required'
    | 'workspace_scope_mismatch'
    | 'module_disabled'
    | 'capability_missing'
    | 'policy_missing'
  capability?: CapabilityId
}>

export function evaluatePermission(
  context: PermissionContext,
  policies: readonly PermissionPolicy[],
): PermissionResult {
  if (context.actorWorkspaceId !== context.target.workspaceId) {
    return { decision: 'DEFAULT_DENY', reasonCode: 'workspace_scope_mismatch' }
  }

  const candidates = policies.filter(
    (policy) => policy.roleId === context.roleId && policy.objectType === context.target.objectType && policy.action === context.action,
  )
  if (candidates.length === 0) return { decision: 'DEFAULT_DENY', reasonCode: 'policy_missing' }

  for (const policy of candidates) {
    if (policy.moduleId && !context.enabledModules.has(policy.moduleId)) {
      return { decision: 'DEFAULT_DENY', reasonCode: 'module_disabled', capability: policy.capability }
    }

    const delegated = context.delegatedCapabilities?.has(policy.capability) ?? false
    const approvalRequired = context.approvalCapabilities?.has(policy.capability) ?? false
    if (!context.capabilities.has(policy.capability) && !delegated && !approvalRequired) {
      return { decision: 'DEFAULT_DENY', reasonCode: 'capability_missing', capability: policy.capability }
    }

    if (approvalRequired || policy.decision === 'APPROVAL_REQUIRED') {
      return { decision: 'APPROVAL_REQUIRED', reasonCode: 'policy_approval_required', capability: policy.capability }
    }
    if (delegated || policy.decision === 'DELEGATED') {
      return { decision: 'DELEGATED', reasonCode: 'policy_delegated', capability: policy.capability }
    }
    return { decision: 'ALLOW', reasonCode: 'policy_allow', capability: policy.capability }
  }

  return { decision: 'DEFAULT_DENY', reasonCode: 'policy_missing' }
}

export type FieldVisibilityRule = Readonly<{
  field: string
  visibility: FieldVisibilityClass
  readableBy: readonly HumanRoleId[]
}>

export function projectVisibleFields<T extends Record<string, unknown>>(
  record: T,
  roleId: HumanRoleId,
  rules: readonly FieldVisibilityRule[],
): Partial<T> {
  const projected: Partial<T> = {}
  for (const rule of rules) {
    if (!rule.readableBy.includes(roleId)) continue
    if (Object.prototype.hasOwnProperty.call(record, rule.field)) {
      projected[rule.field as keyof T] = record[rule.field] as T[keyof T]
    }
  }
  return projected
}

export type TransitionRule<State extends string> = Readonly<{
  from: State
  to: State
  capability: CapabilityId
}>

export function canTransition<State extends string>(
  currentState: State,
  nextState: State,
  capabilities: ReadonlySet<CapabilityId>,
  rules: readonly TransitionRule<State>[],
): boolean {
  const rule = rules.find((candidate) => candidate.from === currentState && candidate.to === nextState)
  return Boolean(rule && capabilities.has(rule.capability))
}
